import * as React from 'react'
import { KeyRound, Plus, ShieldAlert, Trash2 } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/lib/api'
import { useMe, usePlans, useTlds } from '@/lib/queries'
import { useAuth } from '@/lib/auth'
import type { APIToken } from '@/lib/types'
import { AIPanel } from '@/features/settings/AIPanel'
import { PageHeader } from '@/components/layout/AppShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/misc'
import { Tooltip } from '@/components/ui/tooltip'
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states'
import { useToast } from '@/components/ui/toast'
import { DEFAULT_TIME_ZONE, formatDateTime, formatLimit, formatNumber } from '@/lib/utils'

export default function SettingsPage() {
  const { user } = useAuth()
  return (
    <>
      <PageHeader title="系统设置" description="个人偏好、套餐权益、API Token 与平台可用性矩阵。" />
      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile">个人偏好</TabsTrigger>
          <TabsTrigger value="plan">套餐与用量</TabsTrigger>
          <TabsTrigger value="tokens">API Token</TabsTrigger>
          <TabsTrigger value="tlds">后缀可用性</TabsTrigger>
          {user?.role === 'admin' && <TabsTrigger value="ai">AI 语义</TabsTrigger>}
        </TabsList>
        <TabsContent value="profile"><Profile /></TabsContent>
        {user?.role === 'admin' && <TabsContent value="ai"><AIPanel /></TabsContent>}
        <TabsContent value="plan"><PlanUsage /></TabsContent>
        <TabsContent value="tokens"><Tokens /></TabsContent>
        <TabsContent value="tlds"><TldMatrix /></TabsContent>
      </Tabs>
    </>
  )
}

function Profile() {
  const { user, refreshUser } = useAuth()
  const toast = useToast()
  const [displayName, setDisplayName] = React.useState(user?.display_name ?? '')
  const [timezone, setTimezone] = React.useState(user?.timezone ?? DEFAULT_TIME_ZONE)
  const [saving, setSaving] = React.useState(false)

  const zones = React.useMemo(() => {
    try {
      return (Intl as unknown as { supportedValuesOf?: (k: string) => string[] })
        .supportedValuesOf?.('timeZone') ?? ['UTC', 'Asia/Shanghai']
    } catch {
      return ['UTC', 'Asia/Shanghai']
    }
  }, [])

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      await api.patch('/me', { display_name: displayName, timezone })
      await refreshUser()
      toast.success('偏好已保存')
    } catch (err) {
      toast.error('保存失败', err instanceof ApiError ? err.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card className="max-w-lg">
      <CardHeader>
        <CardTitle>个人偏好</CardTitle>
        <CardDescription>时间统一按此时区显示；API 与导出始终使用 UTC。</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">邮箱</Label>
            <Input id="email" value={user?.email ?? ''} disabled />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="display-name">显示名称</Label>
            <Input id="display-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="timezone">时区</Label>
            <select
              id="timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {zones.map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
          </div>
          <Button type="submit" disabled={saving}>保存</Button>
        </form>
      </CardContent>
    </Card>
  )
}

function PlanUsage() {
  const me = useMe()
  const plans = usePlans()

  if (me.isLoading) return <LoadingState rows={4} />
  if (me.error) return <ErrorState error={me.error} onRetry={() => void me.refetch()} />
  if (!me.data) return null

  const { entitlements, usage, enforcement } = me.data

  return (
    <div className="space-y-4">
      {enforcement?.stage && enforcement.stage !== 'none' && (
        <div role="alert" className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
          <div className="flex items-start gap-2">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" aria-hidden />
            <div>
              <p className="text-sm font-medium text-amber-900 dark:text-amber-300">账户当前处于限制状态</p>
              <p className="mt-1 text-xs text-amber-800 dark:text-amber-400">{enforcement.message}</p>
              {enforcement.appealable && (
                <AppealButton />
              )}
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>当前套餐：{entitlements.plan_name}</CardTitle>
          <CardDescription>
            权益由服务端强制执行，界面隐藏不构成配额控制。套餐版本 v{entitlements.plan_version}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Object.values(entitlements.entitlements).map((e) => (
              <div key={e.key} className="rounded-md border p-3">
                <p className="text-xs text-muted-foreground">{e.key}</p>
                <p className="mt-1 text-sm font-medium">
                  {e.enabled != null ? (e.enabled ? '已包含' : '未包含') : formatLimit(e.limit ?? -1)}
                </p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">超额策略：{e.over_policy}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>本期用量</CardTitle>
          <CardDescription>
            周期 {usage.period} · 归因外部成本 {(usage.attributed_cost_micros / 1e6).toFixed(4)} {usage.currency}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {usage.items.map((item) => (
              <div key={item.entitlement} className="flex items-center justify-between rounded-md border p-3 text-sm">
                <span>{item.label}</span>
                <span className="tabular">
                  {formatNumber(item.used)} / {item.unlimited ? '不限' : formatNumber(item.limit)}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {plans.data && (
        <Card>
          <CardHeader>
            <CardTitle>套餐对比</CardTitle>
            <CardDescription>{plans.data.notice}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">
              {/* §18.1: no clickable purchase before payments ship. */}
              支付通道尚未开放，此处仅用于权益说明。
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function AppealButton() {
  const [note, setNote] = React.useState('')
  const [sent, setSent] = React.useState(false)
  const toast = useToast()

  if (sent) return <p className="mt-2 text-xs text-amber-800 dark:text-amber-400">申诉已提交，人工复核中。</p>

  return (
    <form
      className="mt-2 flex gap-2"
      onSubmit={async (e) => {
        e.preventDefault()
        try {
          await api.post('/me/appeal', { note })
          setSent(true)
          toast.success('申诉已提交')
        } catch (err) {
          toast.error('提交失败', err instanceof ApiError ? err.message : undefined)
        }
      }}
    >
      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="说明你的使用场景" className="h-8 text-xs" />
      <Button type="submit" size="sm" variant="outline">提交申诉</Button>
    </form>
  )
}

function Tokens() {
  const qc = useQueryClient()
  const toast = useToast()
  const [name, setName] = React.useState('')
  const [issued, setIssued] = React.useState<string | null>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['tokens'],
    queryFn: () => api.get<{ items: APIToken[] }>('/settings/tokens'),
  })

  async function createToken(e: React.FormEvent) {
    e.preventDefault()
    try {
      const res = await api.post<{ secret: string; notice: string }>('/settings/tokens', { name })
      setIssued(res.secret)
      setName('')
      void qc.invalidateQueries({ queryKey: ['tokens'] })
      toast.warning('请立即保存密钥', res.notice)
    } catch (err) {
      toast.error('创建失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  return (
    <div className="max-w-2xl space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>创建 API Token</CardTitle>
          <CardDescription>密钥仅显示一次，平台只保存哈希值。API 访问属于套餐权益。</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={createToken} className="flex gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Token 名称" required />
            <Button type="submit"><Plus className="h-3.5 w-3.5" aria-hidden /> 创建</Button>
          </form>

          {issued && (
            <div className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
              <p className="text-xs font-medium text-amber-900 dark:text-amber-300">
                密钥仅显示这一次
              </p>
              <code className="mt-1 block break-all rounded bg-background p-2 font-mono text-xs">{issued}</code>
              <Button
                size="sm" variant="outline" className="mt-2"
                onClick={() => { void navigator.clipboard.writeText(issued); toast.success('已复制') }}
              >
                复制
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : isLoading ? (
        <LoadingState rows={2} />
      ) : (data?.items.length ?? 0) === 0 ? (
        <EmptyState icon={KeyRound} title="尚无 API Token" />
      ) : (
        <div className="space-y-2">
          {data!.items.map((t) => (
            <div key={t.id} className="flex items-center gap-3 rounded-lg border p-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{t.name}</p>
                <p className="font-mono text-xs text-muted-foreground">{t.prefix}…</p>
                <p className="text-[11px] text-muted-foreground">
                  创建于 {formatDateTime(t.created_at)}
                  {t.last_used_at && ` · 最后使用 ${formatDateTime(t.last_used_at)}`}
                </p>
              </div>
              {t.revoked_at ? (
                <Badge variant="muted">已撤销</Badge>
              ) : (
                <Button
                  variant="ghost" size="icon-sm"
                  onClick={async () => {
                    await api.delete(`/settings/tokens/${t.id}`)
                    void qc.invalidateQueries({ queryKey: ['tokens'] })
                    toast.success('Token 已撤销')
                  }}
                  aria-label={`撤销 ${t.name}`}
                >
                  <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function TldMatrix() {
  const { data, isLoading, error, refetch } = useTlds()

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />
  if (isLoading) return <LoadingState rows={5} />

  return (
    <Card>
      <CardHeader>
        <CardTitle>后缀可用性矩阵</CardTitle>
        <CardDescription>{data?.notice}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="scroll-x">
          <table className="w-full min-w-[680px] text-sm">
            <caption className="sr-only">已配置后缀及其数据源能力</caption>
            <thead className="border-b">
              <tr>
                {['后缀', '类型', 'Zone 获取', 'Zone 授权', 'RDAP', '批量海选', '状态', '备注'].map((h) => (
                  <th key={h} scope="col" className="whitespace-nowrap px-2 py-2 text-left text-xs font-medium text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data!.tlds.map((t) => (
                <tr key={t.tld} className="border-b last:border-0">
                  <td className="px-2 py-2 font-mono text-xs">.{t.tld}</td>
                  <td className="px-2 py-2 text-xs">{t.kind}</td>
                  <td className="px-2 py-2 text-xs">{t.zone_access}</td>
                  <td className="px-2 py-2">
                    <Badge variant={t.zone_authorized ? 'success' : 'muted'}>
                      {t.zone_authorized ? '已授权' : '未授权'}
                    </Badge>
                  </td>
                  <td className="px-2 py-2">
                    <Badge variant={t.rdap_supported ? 'success' : 'muted'}>
                      {t.rdap_supported ? '支持' : '不支持'}
                    </Badge>
                  </td>
                  <td className="px-2 py-2">
                    <Tooltip content={t.bulk_discovery_supported ? '可参与 Zone Diff 海选' : '无 Zone File 或未授权：仅支持 RDAP 单点查询'}>
                      <Badge variant={t.bulk_discovery_supported ? 'success' : 'warning'}>
                        {t.bulk_discovery_supported ? '支持' : '不支持'}
                      </Badge>
                    </Tooltip>
                  </td>
                  <td className="px-2 py-2">
                    <Badge variant={t.enabled ? 'default' : 'muted'}>{t.enabled ? '已启用' : '未启用'}</Badge>
                  </td>
                  <td className="px-2 py-2 max-w-xs truncate text-xs text-muted-foreground">{t.authorization_note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}
